import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface AttributionModel {
  id: string
  channel: string
  first_touch: number
  last_touch: number
  linear: number
  time_decay: number
  position_based: number
}

interface ConversionPath {
  id: string
  path: string
  conversions: number
  value: number
  avg_touchpoints: number
}

interface Statistics {
  total_conversions: number
  avg_touchpoints: number
  avg_conversion_time: number
  top_first_touch: string
  top_last_touch: string
  attributed_revenue: number
}

export default function MarketingAttributionPage() {
  const [models, setModels] = useState<AttributionModel[]>([])
  const [paths, setPaths] = useState<ConversionPath[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [modelsRes, pathsRes, statsRes] = await Promise.all([
        fetch('/api/marketingattribution/models'),
        fetch('/api/marketingattribution/paths'),
        fetch('/api/marketingattribution/statistics')
      ])
      if (modelsRes.ok) setModels(await modelsRes.json())
      if (pathsRes.ok) setPaths(await pathsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching marketing attribution data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Marketing Attribution</h1>
          <p className="mt-2 text-gray-600">Phase 243 - Attribution modeling and touchpoint analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Conversions</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_conversions}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Touchpoints</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_touchpoints}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Conversion Time</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_conversion_time}d</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top First Touch</p>
              <p className="text-xl font-bold text-green-600">{statistics.top_first_touch}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Last Touch</p>
              <p className="text-xl font-bold text-orange-600">{statistics.top_last_touch}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Attributed Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.attributed_revenue)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Attribution Model Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Touch</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Touch</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linear</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Decay</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position Based</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {models.map((model) => (
                  <tr key={model.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{model.channel}</td>
                    <td className="px-6 py-4 text-blue-600">{formatCurrency(model.first_touch)}</td>
                    <td className="px-6 py-4 text-green-600">{formatCurrency(model.last_touch)}</td>
                    <td className="px-6 py-4 text-purple-600">{formatCurrency(model.linear)}</td>
                    <td className="px-6 py-4 text-orange-600">{formatCurrency(model.time_decay)}</td>
                    <td className="px-6 py-4 text-pink-600 font-bold">{formatCurrency(model.position_based)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Top Conversion Paths</h2>
          </div>
          <div className="p-6 space-y-4">
            {paths.map((path) => (
              <div key={path.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{path.path}</span>
                  <span className="text-lg font-bold text-green-600">{path.conversions}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Value</p>
                    <p className="font-medium text-blue-600">{formatCurrency(path.value)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Touchpoints</p>
                    <p className="font-medium text-purple-600">{path.avg_touchpoints}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Value</p>
                    <p className="font-medium text-green-600">{formatCurrency(path.value / path.conversions)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
